import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError, session } from '../lib/api'
import { useApi } from '../lib/hooks'
import { maskedPhone, money, num, titleCase } from '../lib/format'
import { Banner, Delta, Empty, ErrorNote, IconSearch, Loading, Panel, Pill, StatMini } from '../ui/kit'
import { FormDrawer, type FormFieldDef } from '../ui/FormDrawer'

interface Zone { id: string; code: string; name: string }
interface Centre { id: string; code: string; name: string; zoneId: string }
interface Bundle { zones: Zone[]; collectionCentres: Centre[] }

interface FarmerRow {
  id: string; code: string; fullName: string; msisdn: string; gender: string | null
  kycStatus: string; zoneName: string; payoutRef: string | null; nameMatchState: string | null
  lots90d: number; kg90d: number; value90d: number; avgVariancePct: number | null
  isActive: boolean
}

const KYC_TONE: Record<string, 'ok' | 'warn' | 'crit' | 'neutral'> = {
  verified: 'ok', pending: 'warn', rejected: 'crit', suspended: 'crit',
}

export function Suppliers() {
  // Registering is field work; verifying is an ops control, and the server additionally
  // refuses to let the same person do both for the same farmer.
  const canRegister = session.has('admin', 'ops', 'field_agent')
  const canVerify = session.has('admin', 'ops')

  const [q, setQ] = useState('')
  const [kyc, setKyc] = useState('')
  const [registering, setRegistering] = useState(false)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)

  const params = new URLSearchParams({ pageSize: '100' })
  if (q.trim()) params.set('q', q.trim())
  if (kyc) params.set('kyc', kyc)

  const farmers = useApi<FarmerRow[]>(`/farmers?${params.toString()}`, { deps: [q, kyc] })

  async function verify(f: FarmerRow) {
    if (!window.confirm(
      `Verify ${f.fullName}?

` +
      'You are confirming you have seen their ID and that the M-Pesa name matches it. ' +
      'Money starts going out once you do.',
    )) return
    try {
      await api.post(`/farmers/${f.id}/verify`)
      setBanner({ tone: 'ok', text: `${f.fullName} is verified. Payments can go out.` })
      farmers.reload()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'That did not work.' })
    }
  }

  const rows = farmers.data ?? []
  const verified = rows.filter((f) => f.kycStatus === 'verified').length
  const mismatched = rows.filter((f) => f.nameMatchState === 'mismatch').length
  const totalKg = rows.reduce((s, f) => s + Number(f.kg90d ?? 0), 0)
  const totalValue = rows.reduce((s, f) => s + Number(f.value90d ?? 0), 0)

  // 80/20 supplier concentration — losing three of these would collapse a fill rate.
  const sorted = [...rows].sort((a, b) => Number(b.kg90d) - Number(a.kg90d))
  const topShare = totalKg > 0
    ? (sorted.slice(0, Math.max(1, Math.ceil(rows.length * 0.2))).reduce((s, f) => s + Number(f.kg90d), 0) / totalKg) * 100
    : 0

  return (
    <main className="page">
      <div className="page-head">
        <h1>Farmers</h1>
        <p>
          Everyone you buy from. Money only goes to a farmer whose ID has been checked and whose
          M-Pesa name matches it — no exceptions. You can also see here who actually supplies most
          of your produce.
        </p>
      </div>

      {banner && (
        <div style={{ marginBottom: 'var(--sp-7)' }}>
          <Banner tone={banner.tone === 'ok' ? 'info' : 'crit'} title={banner.text}>{null}</Banner>
        </div>
      )}

      <Panel
        title="Supplier register"
        meta={
          <div className="row" style={{ gap: 8 }}>
            <div className="row" style={{ gap: 6, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '0 8px', background: 'var(--surface)' }}>
              <IconSearch size={14} />
              <input
                className="input"
                style={{ border: 'none', minHeight: 28, padding: '4px 0', width: 200, background: 'transparent' }}
                placeholder="Name, code or phone"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <select className="input" style={{ minHeight: 30, width: 'auto' }} value={kyc} onChange={(e) => setKyc(e.target.value)}>
              <option value="">All KYC states</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
            {canRegister && (
              <button className="btn" onClick={() => setRegistering(true)}>Register farmer</button>
            )}
          </div>
        }
        footer={
          <>
            <StatMini label="Farmers" value={rows.length} note={`${verified} verified`} />
            <StatMini label="Bought in 90 days" value={`${num(totalKg)} kg`} />
            <StatMini label="Paid 90 days" value={money(totalValue, { compact: true })} />
            <StatMini
              label="Top fifth supply"
              value={`${topShare.toFixed(0)}%`}
              note={topShare > 75 ? 'too few farmers - find more' : 'spread around'}
            />
            <StatMini label="M-Pesa name wrong" value={mismatched} note={mismatched > 0 ? 'cannot be paid' : 'none'} />
          </>
        }
      >
        {farmers.error && <ErrorNote error={farmers.error} onRetry={farmers.reload} />}
        {farmers.loading && <Loading rows={6} />}
        {farmers.data?.length === 0 && (
          <Empty
            title="No farmers match"
            action={canRegister ? <button className="btn" onClick={() => setRegistering(true)}>Register farmer</button> : undefined}
          >
            Clear the filters, or register one.
          </Empty>
        )}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Code</th><th>Name</th><th>Area</th><th>Payout</th><th>ID checked</th>
                  <th className="n">Collections 90 d</th><th className="n">Kg 90 d</th>
                  <th className="n">Paid 90 d</th><th className="n">Avg difference</th>
                  {canVerify && <th style={{ width: 80 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id} className={f.nameMatchState === 'mismatch' ? 'attention' : ''}>
                    <td className="code strong">{f.code}</td>
                    <td className="truncate">
                      <Link
                        to={`/console/collections?farmerId=${f.id}&farmerName=${encodeURIComponent(f.fullName)}`}
                        className="row-link"
                        title={`What ${f.fullName} has brought us`}
                      >
                        {f.fullName}
                      </Link>
                    </td>
                    <td className="truncate">{f.zoneName}</td>
                    <td className="mono" style={{ fontSize: 'var(--fs-xs)' }}>
                      {maskedPhone(f.payoutRef ?? f.msisdn)}
                      {f.nameMatchState === 'mismatch' && <> <Pill tone="crit" dot={false}>name mismatch</Pill></>}
                      {f.nameMatchState === 'partial' && <> <Pill tone="warn" dot={false}>partial</Pill></>}
                    </td>
                    <td><Pill tone={KYC_TONE[f.kycStatus] ?? 'neutral'}>{titleCase(f.kycStatus)}</Pill></td>
                    <td className="n">{f.lots90d}</td>
                    <td className="n">{num(f.kg90d)}</td>
                    <td className="n">{money(f.value90d, { currency: false })}</td>
                    <td className="n"><Delta value={f.avgVariancePct} goodDirection="zero" decimals={2} /></td>
                    {canVerify && (
                      <td style={{ textAlign: 'right' }}>
                        {f.kycStatus !== 'verified' && (
                          <button className="btn quiet" style={tinyBtn}
                            onClick={() => void verify(f)}>Verify</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {registering && (
        <RegisterFarmer
          onClose={() => setRegistering(false)}
          onDone={(name) => {
            setRegistering(false)
            setBanner({
              tone: 'ok',
              text: `${name} registered. Someone other than you has to verify them before they can be paid.`,
            })
            farmers.reload()
          }}
        />
      )}
    </main>
  )
}

// ---------------------------------------------------------------- register
function RegisterFarmer({ onClose, onDone }: {
  onClose: () => void
  onDone: (name: string) => void
}) {
  const ref = useApi<Bundle>('/reference/bundle', { cacheKey: 'reference.bundle' })
  const [values, setValues] = useState<Record<string, unknown>>({ consentGiven: false })
  const set = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }))

  const zones = ref.data?.zones ?? []
  const centres = (ref.data?.collectionCentres ?? [])
    .filter((c) => !values.zoneId || c.zoneId === values.zoneId)

  const fields: FormFieldDef[] = [
    { name: 'fullName', label: 'Full name', required: true, hint: 'Exactly as it reads on the ID' },
    {
      name: 'msisdn', label: 'Phone', required: true, half: true, placeholder: '0712 345 678',
      hint: 'They sign in with this, and get paid on it',
    },
    { name: 'nationalId', label: 'National ID', half: true },
    {
      name: 'zoneId', label: 'Zone', type: 'select', required: true, half: true,
      options: zones.map((z) => ({ value: z.id, label: z.name })),
    },
    {
      name: 'collectionCentreId', label: 'Collection centre', type: 'select', half: true,
      options: centres.map((c) => ({ value: c.id, label: c.name })),
    },
    {
      name: 'gender', label: 'Gender', type: 'select', half: true,
      options: [
        { value: 'female', label: 'Female' },
        { value: 'male', label: 'Male' },
        { value: 'other', label: 'Other' },
        { value: 'undisclosed', label: 'Prefer not to say' },
      ],
    },
    { name: 'yearOfBirth', label: 'Year of birth', type: 'number', half: true },
    {
      name: 'payoutAccountName', label: 'Name on the M-Pesa account',
      hint: 'If this does not match the ID, payment is held until somebody sorts it out.',
    },
    {
      name: 'consentGiven', label: 'They agreed to you keeping their details', type: 'bool',
      hint: 'Ask them out loud, in their own language, before ticking this',
    },
  ]

  async function submit() {
    if (!values.consentGiven) throw new Error('Ask for consent before registering someone.')
    const res = await api.post<{ fullName?: string }>('/farmers', {
      fullName: values.fullName,
      msisdn: values.msisdn,
      zoneId: values.zoneId,
      nationalId: values.nationalId || null,
      gender: values.gender || null,
      yearOfBirth: values.yearOfBirth ? Number(values.yearOfBirth) : null,
      collectionCentreId: values.collectionCentreId || null,
      lat: null, lng: null,
      payoutAccountName: values.payoutAccountName || null,
      idPhotoUrl: null, portraitUrl: null,
      consentGiven: true,
    })
    onDone(res.fullName ?? String(values.fullName))
  }

  return (
    <FormDrawer
      kind="Register" title="Farmer" submitLabel="Register"
      fields={fields} values={values} onChange={set}
      onSubmit={submit} onClose={onClose}
    >
      <Banner tone="info" title="Somebody else has to check this farmer">
        A second person looks at the ID and confirms the M-Pesa name matches. That is the whole
        point — on their own, nobody can invent a farmer and start paying them.
      </Banner>
    </FormDrawer>
  )
}

const tinyBtn = { minHeight: 24, padding: '1px 8px', fontSize: 'var(--fs-2xs)' } as const
